import { Box, styled } from '@mui/material';

export const GridContainer = styled(Box)<{ cols: number; rows: number }>(({ theme, cols, rows }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  gridTemplateRows: `repeat(${rows}, auto)`,
  gap: theme.spacing(2),
  width: '100%',
  marginTop: theme.spacing(4),
  position: 'relative',
}));

export const GridCell = styled(Box)<{ isOver?: boolean; isEmpty?: boolean }>(({ theme, isOver, isEmpty }) => ({
  minHeight: '200px',
  position: 'relative',
  backgroundColor: isOver ? theme.palette.action.hover : 'transparent',
  border: isOver ? `2px dashed ${theme.palette.primary.main}` : '2px dashed transparent',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  ...(isEmpty && {
    minHeight: '200px',
  }),
}));

export const GridItemWrapper = styled(Box)<{
  isDragging?: boolean;
  gridColumn: number;
  gridRow: number;
  gridColumnSpan: number;
  gridRowSpan: number;
  isResizing?: boolean;
}>(({ theme, isDragging, gridColumn, gridRow, gridColumnSpan, gridRowSpan, isResizing }) => ({
  gridColumn: `${gridColumn} / span ${gridColumnSpan}`,
  gridRow: `${gridRow} / span ${gridRowSpan}`,
  minHeight: '200px',
  opacity: isDragging || isResizing ? 0.5 : 1,
  cursor: isDragging ? 'grabbing' : 'grab',
  '&[data-locked="true"]': {
    cursor: 'default',
  },
  touchAction: 'none',
  transition: isDragging || isResizing ? 'none' : 'opacity 0.2s ease, transform 0.2s ease',
  zIndex: isDragging || isResizing ? 1000 : 1,
  position: 'relative',
  '&:hover': {
    '& > *': {
      boxShadow: theme.shadows[4],
    },
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '12px',
  height: '12px',
  backgroundColor: theme.palette.primary.main,
  border: `2px solid ${theme.palette.background.paper}`,
  borderRadius: '50%',
  cursor: 'nwse-resize',
  zIndex: 1001,
  opacity: 0,
  transition: 'opacity 0.2s ease',
  transformOrigin: 'center',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.2)',
  },
  '&.visible': {
    opacity: 0.8,
  },
}));

export const ResizeHandleSE = styled(ResizeHandle)({
  bottom: '-6px',
  right: '-6px',
  cursor: 'nwse-resize',
  transformOrigin: 'center',
  '&:hover': {
    opacity: 1,
    transform: 'scale(1.2)',
  },
});

export const ResizeHandleE = styled(ResizeHandle)({
  top: '50%',
  right: '-6px',
  transform: 'translateY(-50%)',
  transformOrigin: 'center',
  cursor: 'ew-resize',
  width: '8px',
  height: '24px',
  borderRadius: '4px',
  '&:hover': {
    opacity: 1,
    transform: 'translateY(-50%) scale(1.2)',
  },
});

export const ResizeHandleS = styled(ResizeHandle)({
  bottom: '-6px',
  left: '50%',
  transform: 'translateX(-50%)',
  transformOrigin: 'center',
  cursor: 'ns-resize',
  width: '24px',
  height: '8px',
  borderRadius: '4px',
  '&:hover': {
    opacity: 1,
    transform: 'translateX(-50%) scale(1.2)',
  },
});

export const ResizePreview = styled(Box)<{ previewW: number; previewH: number; cellWidth: number; cellHeight: number }>(
  ({ theme, previewW, previewH, cellWidth, cellHeight }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: `${previewW * cellWidth + (previewW - 1) * 16}px`, // width + gaps
    height: `${previewH * cellHeight + (previewH - 1) * 16}px`, // height + gaps
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    opacity: 0.3,
    pointerEvents: 'none',
    zIndex: 1002,
    transition: 'none', // No transition for smooth preview updates
  })
);
